import React, { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import DsRoot from 'src/component/ds/DsRoot';
import useDsScreen from 'src/component/ds/useDsScreen';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'src/component/ds/ui/sheet';

interface DsPanelSlotValue {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
}

const DsPanelSlotContext = createContext<DsPanelSlotValue | null>(null);

function useDsPanelSlot () {
  const ctx = useContext(DsPanelSlotContext);
  if (!ctx) {
    throw new Error('DsPanel must be used inside a v2 screen (DsPanelProvider)');
  }
  return ctx;
}

/** Mounted by ProtectedRoute for v2 routes only. */
export function DsPanelProvider ({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);

  return <DsPanelSlotContext.Provider value={value}>{children}</DsPanelSlotContext.Provider>;
}

/** Where a docked panel lands, beside the content. `contents` makes the panel itself the flex item. */
export function DsPanelHost () {
  const { setSlot } = useDsPanelSlot();

  return <div ref={setSlot} className='ds-contents' />;
}

export interface DsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  side?: 'left' | 'right';
  children: React.ReactNode;
}

/**
 * Side panel, rendered from the screen's own JSX so its children keep the
 * screen's props and providers. Docked beside the content on wide screens,
 * floating over it below ds-pair.
 */
export function DsPanel ({ open, ...props }: DsPanelProps) {
  if (!open) {
    return null;
  }

  return <OpenPanel {...props} />;
}

/* Split out so the breakpoint listener only exists while a panel is open. */
function OpenPanel ({ onOpenChange, title, side = 'right', children }: Omit<DsPanelProps, 'open'>) {
  const { slot } = useDsPanelSlot();
  const docked = useDsScreen('ds-pair');

  const sheet = (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent side={side} docked={docked} aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className='ds-flex-1 ds-px-4 ds-pb-4'>{children}</div>
      </SheetContent>
    </Sheet>
  );

  if (!docked) {
    return sheet;
  }

  if (!slot) {
    return null;
  }

  return createPortal(
    <DsRoot className={side === 'left' ? 'ds-order-first ds-h-full' : 'ds-h-full'}>{sheet}</DsRoot>,
    slot
  );
}
